/* 3Yes heat pump sizing calculator.
 *
 * Two halves. ThreeYesCalc.recommendKw and ThreeYesCalc.matchProducts are pure
 * functions with no DOM in them, so they can be tested directly. ThreeYesCalc.init
 * wires one calculator element to those functions and to the catalogue loader.
 *
 * Catalogue records arrive in the shape produced by templates/collection.calc-data.liquid:
 *   h  handle
 *   t  title
 *   k  heating kW           (custom.heating_kw)
 *   c  cooling kW           (custom.cooling_kw)
 *   p  price in cents       (product.price, GST inclusive)
 *   f  family               (custom.family)
 *   i  install price, cents (custom.install_price)
 *   a  available
 *
 * Every coefficient in COEFFICIENTS is listed in docs/calculator-plan.md with a note
 * on where it came from. None of them is drawn from a published standard. They are
 * held in one object so Shamal's sign-off changes one block, not the logic.
 */
window.ThreeYesCalc = (function () {
  'use strict';

  var COEFFICIENTS = {
    /* Watts of heating capacity per cubic metre of room, at the reference case:
       a living room, average insulation and sun. Set so the volumetric formula
       reproduces the concept tool's reference output at a 2.4 m ceiling.
       Not sourced from a standard. Needs Shamal's sign-off. */
    wattsPerCubicMetre: 90,

    /* Room use. Living room is the reference at 1.00. */
    roomType: {
      bedroom: 0.9,
      living: 1.0,
      'open-plan': 1.15
    },

    /* Insulation and sun, asked as one question. Average is the reference at 1.00. */
    conditions: {
      good: 0.9,
      average: 1.0,
      poor: 1.2
    },

    /* Ceiling height is taken as measured, but clamped so a typo cannot produce
       an absurd recommendation. */
    minCeiling: 2.0,
    maxCeiling: 5.0,
    minRoomSide: 1.0,
    maxRoomSide: 30.0,

    /* A unit is a match if its heating kW sits between the requirement and the
       requirement times this figure. Above it the unit is oversized for the room
       and will short cycle. Needs Shamal's sign-off. */
    headroom: 1.3,

    /* If nothing matches inside the headroom window, widen in these steps before
       giving up, so a customer still sees the nearest real options. */
    widenSteps: [1.5, 1.8, 2.2]
  };

  function clamp(value, low, high) {
    if (!isFinite(value)) return low;
    return Math.min(high, Math.max(low, value));
  }

  function roundKw(value) {
    return Math.round(value * 10) / 10;
  }

  /* Recommend a heating capacity in kW.
   *
   * input: { length, width, height, roomType, conditions }
   * length, width and height are metres. roomType and conditions are keys of
   * COEFFICIENTS.roomType and COEFFICIENTS.conditions.
   *
   * kW = length x width x height x wattsPerCubicMetre x roomType x conditions / 1000
   */
  function recommendKw(input) {
    var c = COEFFICIENTS;
    var length = clamp(parseFloat(input.length), c.minRoomSide, c.maxRoomSide);
    var width = clamp(parseFloat(input.width), c.minRoomSide, c.maxRoomSide);
    var height = clamp(parseFloat(input.height), c.minCeiling, c.maxCeiling);

    var roomFactor = c.roomType[input.roomType];
    if (typeof roomFactor !== 'number') roomFactor = c.roomType.living;

    var conditionFactor = c.conditions[input.conditions];
    if (typeof conditionFactor !== 'number') conditionFactor = c.conditions.average;

    var volume = length * width * height;
    var watts = volume * c.wattsPerCubicMetre * roomFactor * conditionFactor;

    return {
      kw: roundKw(watts / 1000),
      area: Math.round(length * width * 10) / 10,
      volume: Math.round(volume * 10) / 10,
      length: length,
      width: width,
      height: height,
      roomFactor: roomFactor,
      conditionFactor: conditionFactor
    };
  }

  /* Pick the units that fit.
   *
   * products: array of catalogue records
   * kw:       the recommended capacity from recommendKw
   * options:  { families: [...], limit: n }
   *
   * Returns { status, products, low, high, ceiling }
   *   status 'ok'        matched inside the headroom window
   *   status 'widened'   nothing inside the window, showing the nearest larger units
   *   status 'over'      the room needs more than the biggest unit in these families
   *   status 'empty'     the catalogue has no usable products at all
   */
  function matchProducts(products, kw, options) {
    var opts = options || {};
    var families = opts.families || ['high-wall', 'floor-console'];
    var limit = opts.limit || 6;

    var pool = (products || []).filter(function (p) {
      return p && typeof p.k === 'number' && p.k > 0 && typeof p.p === 'number' &&
        families.indexOf(p.f) > -1 && p.a !== false;
    });

    if (!pool.length) {
      return { status: 'empty', products: [], low: kw, high: kw, ceiling: 0 };
    }

    var ceiling = pool.reduce(function (max, p) { return p.k > max ? p.k : max; }, 0);

    function inWindow(multiplier) {
      var high = roundKw(kw * multiplier);
      return {
        high: high,
        hits: pool.filter(function (p) { return p.k >= kw && p.k <= high; })
      };
    }

    var attempt = inWindow(COEFFICIENTS.headroom);
    var status = 'ok';

    if (!attempt.hits.length) {
      for (var i = 0; i < COEFFICIENTS.widenSteps.length; i += 1) {
        attempt = inWindow(COEFFICIENTS.widenSteps[i]);
        if (attempt.hits.length) { status = 'widened'; break; }
      }
    }

    if (!attempt.hits.length) {
      return { status: 'over', products: [], low: kw, high: attempt.high, ceiling: ceiling };
    }

    /* Cheapest unit that fits comes first. Every unit shown already sits inside the
       capacity window, so price is the useful ordering for a shopper. The heating kW
       is on every row, so nothing is hidden by sorting this way. */
    attempt.hits.sort(function (a, b) {
      if (a.p !== b.p) return a.p - b.p;
      return a.k - b.k;
    });

    return {
      status: status,
      products: attempt.hits.slice(0, limit),
      low: kw,
      high: attempt.high,
      ceiling: ceiling
    };
  }

  /* ---------- presentation helpers ---------- */

  function formatMoney(centsValue) {
    if (typeof centsValue !== 'number') return '';
    var dollars = centsValue / 100;
    var whole = Math.round(dollars);
    return '$' + String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function familyLabel(family) {
    if (family === 'high-wall') return 'High wall';
    if (family === 'floor-console') return 'Floor console';
    if (family === 'ducted') return 'Ducted';
    return family || '';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ---------- catalogue loading ---------- */

  /* sources is an array of { collection, pages }. Each page is fetched from the
     alternate collection template, which returns one JSON object per page. All
     pages are requested at once because the page count is rendered by Liquid, so
     there is no discovery round trip. */
  /* The feed is an alternate collection template, so it only exists on the theme that
     defines it. On an unpublished theme the storefront serves the live theme unless the
     preview_theme_id travels with the request, which would hand us the ordinary collection
     page as HTML. Carrying the parameter through keeps preview and production identical. */
  function feedUrl(handle, page) {
    var url = '/collections/' + handle + '?view=calc-data&page=' + page;
    var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url += '&preview_theme_id=' + encodeURIComponent(preview);
    return url;
  }

  function loadCatalogue(sources, cacheKey) {
    var cached = readCache(cacheKey);
    if (cached) return Promise.resolve(cached);

    var requests = [];
    sources.forEach(function (source) {
      var pages = Math.max(1, parseInt(source.pages, 10) || 1);
      for (var page = 1; page <= pages; page += 1) {
        requests.push(
          /* No Accept header is set. The storefront answers 404 to an alternate
             template when Accept is text/plain, and the browser default is served
             correctly. */
          fetch(feedUrl(source.collection, page))
            .then(function (response) {
              if (!response.ok) throw new Error('HTTP ' + response.status);
              return response.text();
            })
            .then(function (text) { return JSON.parse(text); })
        );
      }
    });

    return Promise.all(requests).then(function (payloads) {
      var merged = [];
      var seen = {};
      payloads.forEach(function (payload) {
        (payload.products || []).forEach(function (product) {
          if (!seen[product.h]) { seen[product.h] = true; merged.push(product); }
        });
      });
      writeCache(cacheKey, merged);
      return merged;
    });
  }

  function readCache(key) {
    if (!key) return null;
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, value) {
    if (!key) return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Private browsing or a full quota. The calculator works without the cache. */
    }
  }

  /* ---------- wiring ---------- */

  function init(root, options) {
    if (!root || root.dataset.yCalcReady === 'true') return null;
    root.dataset.yCalcReady = 'true';

    var opts = options || {};
    var fields = {
      length: root.querySelector('[data-len]'),
      width: root.querySelector('[data-wid]'),
      height: root.querySelector('[data-hgt]'),
      conditions: root.querySelector('[data-cond]')
    };
    var segment = root.querySelector('[data-seg]');
    var kwOut = root.querySelector('[data-kw]');
    var noteOut = root.querySelector('[data-note]');
    var listOut = root.querySelector('[data-results]');
    var statusOut = root.querySelector('[data-status]');
    var moreLink = root.querySelector('[data-more]');

    var roomType = 'living';
    var catalogue = null;
    var catalogueState = 'loading';

    function currentInput() {
      return {
        length: fields.length ? fields.length.value : 0,
        width: fields.width ? fields.width.value : 0,
        height: fields.height ? fields.height.value : 2.4,
        roomType: roomType,
        conditions: fields.conditions ? fields.conditions.value : 'average'
      };
    }

    function render() {
      var result = recommendKw(currentInput());
      if (kwOut) kwOut.textContent = result.kw.toFixed(1) + ' kW';
      if (noteOut) {
        noteOut.textContent =
          result.area.toFixed(1) + ' m² floor area, ' +
          result.volume.toFixed(1) + ' m³ of air to heat';
      }

      if (moreLink && opts.browseUrl) {
        moreLink.href = opts.browseUrl;
      }

      if (catalogueState === 'loading') {
        setStatus('Loading the range.');
        if (listOut) listOut.innerHTML = '';
        return;
      }

      if (catalogueState === 'failed') {
        setStatus(
          'We could not load the range just now. Your recommended size is ' +
          result.kw.toFixed(1) + ' kW. Browse the heat pumps and look for that heating capacity, ' +
          'or call 0800 393 722 and we will find it for you.'
        );
        if (listOut) listOut.innerHTML = '';
        return;
      }

      var match = matchProducts(catalogue, result.kw, {
        families: opts.families,
        limit: opts.limit
      });
      paint(match, result);
    }

    function setStatus(text) {
      if (!statusOut) return;
      statusOut.textContent = text || '';
      statusOut.hidden = !text;
    }

    function paint(match, result) {
      if (!listOut) return;

      if (match.status === 'empty') {
        listOut.innerHTML = '';
        setStatus(
          'The range is not loaded yet. Your recommended size is ' +
          result.kw.toFixed(1) + ' kW. Call 0800 393 722 and we will match it for you.'
        );
        return;
      }

      if (match.status === 'over') {
        listOut.innerHTML = '';
        setStatus(
          'A room this size needs more than a single wall or floor unit can give. ' +
          'The largest single unit we stock is ' + match.ceiling.toFixed(1) + ' kW. ' +
          'A ducted or multi split system is the usual answer. Call 0800 393 722 and we will size it properly.'
        );
        return;
      }

      if (match.status === 'widened') {
        setStatus(
          'Nothing sits exactly on ' + result.kw.toFixed(1) + ' kW, so these are the nearest units above it.'
        );
      } else {
        setStatus('');
      }

      var html = match.products.map(function (product) {
        var install = typeof product.i === 'number'
          ? '<span class="y-res__install">Installed from ' + escapeHtml(formatMoney(product.p + product.i)) + '</span>'
          : '';
        return '' +
          '<li class="y-res">' +
          '<a class="y-res__link" href="/products/' + escapeHtml(product.h) + '">' +
          '<span class="y-res__meta">' + escapeHtml(product.k.toFixed(1)) + ' kW heating' +
          (typeof product.c === 'number' ? ' · ' + escapeHtml(product.c.toFixed(1)) + ' kW cooling' : '') +
          ' · ' + escapeHtml(familyLabel(product.f)) + '</span>' +
          '<span class="y-res__title">' + escapeHtml(product.t) + '</span>' +
          '<span class="y-res__price">' + escapeHtml(formatMoney(product.p)) + ' including GST' + '</span>' +
          install +
          '</a>' +
          '</li>';
      }).join('');

      listOut.innerHTML = html;
    }

    if (segment) {
      segment.addEventListener('click', function (event) {
        var button = event.target.closest('button');
        if (!button) return;
        Array.prototype.forEach.call(segment.children, function (child) {
          child.setAttribute('aria-pressed', 'false');
        });
        button.setAttribute('aria-pressed', 'true');
        roomType = button.dataset.room || 'living';
        render();
      });
    }

    Object.keys(fields).forEach(function (key) {
      var field = fields[key];
      if (!field) return;
      field.addEventListener('input', render);
      field.addEventListener('change', render);
    });

    var loader = opts.load
      ? opts.load()
      : loadCatalogue(opts.sources || [], opts.cacheKey);

    loader
      .then(function (products) {
        catalogue = products || [];
        catalogueState = catalogue.length ? 'ready' : 'ready';
        render();
      })
      .catch(function () {
        catalogueState = 'failed';
        render();
      });

    render();

    return { render: render, recommendKw: recommendKw };
  }

  return {
    COEFFICIENTS: COEFFICIENTS,
    recommendKw: recommendKw,
    matchProducts: matchProducts,
    loadCatalogue: loadCatalogue,
    formatMoney: formatMoney,
    familyLabel: familyLabel,
    init: init
  };
})();
